import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEOHead = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const baseUrl = 'https://bennaid.com';
  const ogLocaleMap = { ar: 'ar_MA', fr: 'fr_FR', es: 'es_ES', en: 'en_US' };

  // Geo targeting adapts per language: Spanish page targets Spain, others Morocco
  const geo = currentLang === 'es'
    ? { region: 'ES', placename: 'Madrid, Spain', lat: '40.4168', lon: '-3.7038' }
    : { region: 'MA', placename: 'Casablanca, Morocco', lat: '33.5731', lon: '-7.5898' };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Oualid BEN NAID",
    "description": t('meta.description'),
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "image": `${baseUrl}/og-image.jpg`,
    "telephone": "+212661907443",
    "email": "oualid@bennaid.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Casablanca",
      "addressCountry": "MA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "33.5731",
      "longitude": "-7.5898"
    },
    "areaServed": [
      { "@type": "Country", "name": "Morocco" },
      { "@type": "Country", "name": "Spain" },
      { "@type": "City", "name": "Casablanca" },
      { "@type": "City", "name": "Rabat" },
      { "@type": "City", "name": "Marrakech" },
      { "@type": "City", "name": "Madrid" },
      { "@type": "City", "name": "Barcelona" },
      { "@type": "City", "name": "Valencia" },
      { "@type": "AdministrativeArea", "name": "Andalusia" }
    ],
    "priceRange": "$$$$",
    "openingHours": "Mo-Fr 09:00-18:00",
    "sameAs": [
      "https://www.linkedin.com/in/oualidbennaid/"
    ],
    "knowsAbout": [
      "Digital Transformation",
      "Artificial Intelligence Implementation",
      "SEO Optimization",
      "Customer Loyalty Systems",
      "Cybersecurity Training",
      "Cybersecurity Implementation",
      "Business Automation",
      "Process Optimization"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Consulting Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Digital Transformation Consulting",
            "description": "End-to-end digital transformation and AI implementation services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SEO Consulting",
            "description": "Technical SEO audits and search visibility strategy"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Customer Loyalty Systems",
            "description": "Data-driven loyalty programs and retention strategies"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cybersecurity Training & Implementation",
            "description": "Personal and team cybersecurity training and end-to-end implementation of security solutions"
          }
        }
      ]
    }
  };

  // Person Schema
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Oualid BEN NAID",
    "jobTitle": "Digital Transformation Consultant",
    "description": "Expert digital transformation consultant with 20+ years of experience in AI implementation, SEO, and customer loyalty systems.",
    "url": baseUrl,
    "image": `${baseUrl}/og-image.jpg`,
    "email": "oualid@bennaid.com",
    "telephone": "+212661907443",
    "sameAs": ["https://www.linkedin.com/in/oualidbennaid/"],
    "nationality": { "@type": "Country", "name": "Morocco" },
    "worksFor": {
      "@type": "Organization",
      "name": "Oualid BEN NAID Consulting"
    },
    "knowsAbout": [
      "Digital Transformation",
      "Artificial Intelligence",
      "SEO",
      "Customer Loyalty",
      "Cybersecurity",
      "Business Strategy"
    ]
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      }
    ]
  };

  const alternateUrls = [
    { lang: 'en', url: baseUrl },
    { lang: 'fr', url: `${baseUrl}/fr` },
    { lang: 'es', url: `${baseUrl}/es` },
    { lang: 'ar', url: `${baseUrl}/ar` },
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} dir={currentLang === 'ar' ? 'rtl' : 'ltr'} />
      <title>{t('meta.title')}</title>
      <meta name="description" content={t('meta.description')} />
      <meta name="keywords" content={t('meta.keywords')} />
      <meta name="author" content="Oualid BEN NAID" />
      <meta name="robots" content="index, follow" />
      
      {/* Geo Tags (adapts per language) */}
      <meta name="geo.region" content={geo.region} />
      <meta name="geo.placename" content={geo.placename} />
      <meta name="geo.position" content={`${geo.lat};${geo.lon}`} />
      <meta name="ICBM" content={`${geo.lat}, ${geo.lon}`} />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={t('meta.title')} />
      <meta property="og:description" content={t('meta.description')} />
      <meta property="og:url" content={currentLang === 'en' ? baseUrl : `${baseUrl}/${currentLang}`} />
      <meta property="og:site_name" content="Oualid BEN NAID" />
      <meta property="og:locale" content={ogLocaleMap[currentLang] || 'en_US'} />
      <meta property="og:image" content={`${baseUrl}/og-image.jpg`} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t('meta.title')} />
      <meta name="twitter:description" content={t('meta.description')} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.jpg`} />
      
      {/* Canonical and Alternate URLs */}
      <link rel="canonical" href={currentLang === 'en' ? baseUrl : `${baseUrl}/${currentLang}`} />
      {alternateUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
      
      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
